import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Swal from 'sweetalert2';
import { verifyEmail } from "../services/companyAuthService";

const VerifyEmail = () => {
  const [otp, setOtp] = useState(new Array(6).fill(""));
  const [isVerifying, setIsVerifying] = useState(false);
  const inputRefs = useRef([]);
  const navigate = useNavigate();

  useEffect(() => {
    // Focus the first input on component mount
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, []);

  const handleChange = (element, index) => {
    const value = element.value.replace(/\D/g, ""); // Allow only digits

    if (!value) {
      // Handle empty/backspace case
      const newOtp = [...otp];
      newOtp[index] = "";
      setOtp(newOtp);
      return;
    }

    // Handle paste event
    if (value.length > 1) {
      // User pasted a value
      const pastedValues = value.slice(0, 6).split("");
      const newOtp = [...otp];

      // Fill the current and subsequent inputs
      for (let i = 0; i < pastedValues.length && index + i < 6; i++) {
        newOtp[index + i] = pastedValues[i];
      }

      setOtp(newOtp);

      // Focus the next empty input or the last input
      const nextIndex = Math.min(index + pastedValues.length, 5);
      if (inputRefs.current[nextIndex]) {
        inputRefs.current[nextIndex].focus();
      }
    } else {
      // Single digit input
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);

      // Move to next input if available
      if (index < 5 && inputRefs.current[index + 1]) {
        inputRefs.current[index + 1].focus();
      }
    }
  };

  const handleKeyDown = (e, index) => {
    // Handle backspace
    if (e.key === "Backspace") {
      e.preventDefault();

      const newOtp = [...otp];

      if (newOtp[index]) {
        // Clear current input if it has a value
        newOtp[index] = "";
        setOtp(newOtp);
      } else if (index > 0) {
        // Move to previous input if current is empty
        newOtp[index - 1] = "";
        setOtp(newOtp);
        inputRefs.current[index - 1].focus();
      }
    }

    // Handle left arrow
    if (e.key === "ArrowLeft" && index > 0) {
      inputRefs.current[index - 1].focus();
    }

    // Handle right arrow
    if (e.key === "ArrowRight" && index < 5) {
      inputRefs.current[index + 1].focus();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isVerifying) return;

    const verificationCode = otp.join("");

    if (verificationCode.length !== 6) {
      Swal.fire({
        icon: "error",
        title: "Invalid Code",
        text: "Please enter all 6 digits of the verification code",
        confirmButtonColor: "#f97316"
      });
      return;
    }

    setIsVerifying(true);
    try {
      await verifyEmail(verificationCode);
      Swal.fire({
        icon: "success",
        title: "Success!",
        text: "Email verified successfully",
        confirmButtonColor: "#f97316"
      });
      navigate("/company/login");
    } catch (err) {
      setIsVerifying(false);
      Swal.fire({
        icon: "error",
        title: "Verification Failed",
        text: err.response?.data?.message || "Failed to verify email. Please try again.",
        confirmButtonColor: "#f97316"
      });
    }
  };

  const handlePaste = (e, index) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (!pastedData) return;

    const newOtp = [...otp];
    for (let i = 0; i < pastedData.length && index + i < 6; i++) {
      newOtp[index + i] = pastedData[i];
    }
    setOtp(newOtp);

    // Focus the next empty input or the last input
    const nextIndex = Math.min(index + pastedData.length, 5);
    if (inputRefs.current[nextIndex]) {
      inputRefs.current[nextIndex].focus();
    }
  };

  return (
    <div className="flex items-center justify-center flex-col gap-4 h-[calc(100dvh-80px)] bg-gray-100 px-2 md:px-4 overflow-hidden">
      <div className="bg-white p-2 md:p-4 rounded-xl shadow-lg w-full max-w-sm sm:max-w-md lg:max-w-xl">
        <div className="text-center mb-8">
          <h5 className="text-gray-700 font-bold text-xs md:text-sm mb-2">Verify Your Email</h5>
          <p className="text-xs md:text-sm text-gray-500">Enter the 6-digit code sent to your email</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="flex justify-center gap-2 md:gap-4">
            {otp.map((digit, index) => (
              <input
                key={index}
                ref={(el) => (inputRefs.current[index] = el)}
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={digit}
                onChange={(e) => handleChange(e.target, index)}
                onKeyDown={(e) => handleKeyDown(e, index)}
                onPaste={(e) => handlePaste(e, index)}
                className="w-9 h-9 lg:w-14 lg:h-14 text-center text-lg lg:text-xl font-semibold 
                         border-2 rounded-lg focus:border-orange-500 focus:outline-none
                         transition-all duration-200 bg-white
                         disabled:bg-gray-50 disabled:text-gray-400
                         hover:border-orange-200"
                required
              />
            ))}
          </div>

          <button
            type="submit"
            disabled={isVerifying}
            className="w-full bg-orange-500 text-white py-3 rounded-lg 
                     hover:bg-orange-600 active:bg-orange-700
                     transition duration-200 text-xs md:text-sm font-medium
                     focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2
                     disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:bg-orange-500 disabled:active:bg-orange-500"
          >
            {isVerifying ? "Verifying..." : "Verify Email"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default VerifyEmail; 